import { FrontendRestaurant } from './restaurantService';
import { getUserDefault, setUserDefault } from '../userdefaults/userDefaultService';
import {
  useAppUserDefaultStore,
  ALL_RESTAURANTS_ID,
  AllRestaurantsOption,
  createAllRestaurantsOption
} from '../../stores/appUserDefaultStore';
import {
  isImpersonating,
  getImpersonationRestaurantId,
  setImpersonationRestaurantId
} from '../../stores/appImpersonationStore';

const RESTAURANT_KEY = 'selected_restaurant_id';

/**
 * Load and set the default restaurant selection from user preferences.
 * Handles "All Restaurants" option for users with multiple restaurants.
 */
export async function loadDefaultRestaurant(
  restaurants: FrontendRestaurant[],
  userId: string
): Promise<void> {
  if (!restaurants.length) return;

  const hasMultipleRestaurants = restaurants.length >= 2;
  const allRestaurantsOption = hasMultipleRestaurants ? createAllRestaurantsOption(userId) : null;
  const selectOptions = hasMultipleRestaurants && allRestaurantsOption
    ? [allRestaurantsOption, ...restaurants]
    : restaurants;

  try {
    const def = await getUserDefault(RESTAURANT_KEY);
    // While impersonating, a local pick wins over the account's stored default.
    const storedValue = getImpersonationRestaurantId() ?? def?.value;
    let initial;

    // Check if saved value is "All Restaurants"
    if (storedValue === ALL_RESTAURANTS_ID && allRestaurantsOption) {
      initial = allRestaurantsOption;
    } else {
      // Find restaurant by ID, fallback to first option
      initial = storedValue && selectOptions.find(r => r.id === storedValue)
        ? selectOptions.find(r => r.id === storedValue)!
        : selectOptions[0];
    }

    useAppUserDefaultStore.getState().setRestaurant(initial);

    // Save if it's different from stored value — never while impersonating, that
    // would move the real user's selected restaurant under them.
    if (!isImpersonating() && (!def || def.value !== initial.id)) {
      await setUserDefault(RESTAURANT_KEY, initial.id);
    }
  } catch (error) {
    console.warn('Failed to load default restaurant, using first option');
    useAppUserDefaultStore.getState().setRestaurant(selectOptions[0]);
  }
}

/**
 * Save restaurant selection to user preferences. While a super admin is
 * impersonating, the pick stays local to this browser instead — the impersonated
 * account keeps its own default restaurant.
 */
export async function saveDefaultRestaurant(
  restaurant: FrontendRestaurant | AllRestaurantsOption
): Promise<void> {
  if (isImpersonating()) {
    setImpersonationRestaurantId(restaurant.id);
    useAppUserDefaultStore.getState().setRestaurant(restaurant);
    return;
  }

  try {
    await setUserDefault(RESTAURANT_KEY, restaurant.id);
    useAppUserDefaultStore.getState().setRestaurant(restaurant);
  } catch (error) {
    console.error('Failed to save default restaurant:', error);
    throw error;
  }
}

/**
 * Get the list of selectable restaurant options including "All Restaurants" if applicable.
 */
export function getRestaurantSelectOptions(
  restaurants: FrontendRestaurant[],
  userId: string
): (FrontendRestaurant | AllRestaurantsOption)[] {
  const hasMultipleRestaurants = restaurants.length >= 2;
  const allRestaurantsOption = hasMultipleRestaurants ? createAllRestaurantsOption(userId) : null;

  return hasMultipleRestaurants && allRestaurantsOption
    ? [allRestaurantsOption, ...restaurants]
    : restaurants;
}
